import React, { useState, useEffect, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { Form, Input, Button, Radio, Icon, Statistic, Collapse, Card, Typography, Result } from 'antd';
import { EbPageLoader } from '../../Common';
import { EASEBUZZ_PS_LOGO_COLOR } from '../../../constants';
import { Beneficiary } from '../../../models';
import { MerchantService } from '../../../services';
import { handleApiExceptions, beneficiaryNameRegex, printRupee } from '../../../app_methods';
import { Text1, Text2, Text3, Text4 } from '../../../styles';
import moment from 'moment';
import "./index.scss";


const PayoutLinkPaymentAttemptForm = (props) => {
    const { form } = props;
    const { getFieldDecorator } = form;
    const { validations } = Beneficiary;

    const [isLoading, setIsLoading] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [merchant, setMerchant] = useState({});
    const [payoutLink, setPayoutLink] = useState({});
    const [activePanel, setActivePanel] = useState("0");
    const [authKey, setAuthKey] = useState("");
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [countdown, setCountdown] = useState(moment());
    const [showResetButton, setShowResetButton] = useState(false);
    const [isGeneratingOTP, setIsGeneratingOTP] = useState(false);
    const [OTPSent, setOTPSent] = useState(false);

    const authInput = useRef(null);

    const resetCountdown = () => setCountdown(moment().add(180, "seconds"));

    const bankAccountTypes = ["bank_account", "imps", "neft", "rtgs"];
    const isBankTypeAllowed = payoutLink.allowed_beneficiary_types?.find((type) => bankAccountTypes.includes(type));
    const isUpiTypeAllowed = payoutLink.allowed_beneficiary_types?.includes("upi");

    const handleResend = () => {
        setAuthKey("");
        authInput.current.setValue("");
        generateOTP();
        setShowResetButton(false);
        resetCountdown();
    }

    const getPayoutLinkInfo = () => {
        MerchantService.get_payout_link_info({
            code: props.match.params.white_label_code,
            ulc: props.match.params.ulc
        }).then(response => {
            if (response.success) {
                setMerchant(response.data.merchant);
                setPayoutLink(response.data.payout_link);
                setIsMounted(true);
                if (response.data.payout_link.status == "active" && !response.data.payout_link.is_attempt_in_process) {
                    const selectedBeneficiaryType = response.data.payout_link.allowed_beneficiary_types?.find(
                            (type) => bankAccountTypes.includes(type),
                        )
                            ? "bank_account"
                            : "upi";
                    form.setFieldsValue({ "beneficiary_type": selectedBeneficiaryType });
                    if (!response.data.payout_link.bypass_authentication) setOTPSent(true);
                }
            }
            else {
                handleApiExceptions("Something went wrong! Please try again.");
            }
        }).catch(error => {
            handleApiExceptions("Something went wrong! Please try again.");
        });
    };
    
    const generateOTP = () => {
        setIsGeneratingOTP(true);
        MerchantService.generate_payout_link_otp({
            code: props.match.params.white_label_code,
            ulc: props.match.params.ulc,
            session_id: payoutLink.session_id
        }).then(response => {
            setIsGeneratingOTP(false);
            if (response.success) {
                setOTPSent(true);
                resetCountdown();
                const selectedBeneficiaryType = payoutLink.allowed_beneficiary_types?.find(
                            (type) => bankAccountTypes.includes(type),
                        )
                            ? "bank_account"
                            : "upi";
                form.setFieldsValue({ "beneficiary_type": selectedBeneficiaryType });
            }
            else handleApiExceptions(response.message || "Something went wrong.");
        }).catch(error => {
            setIsGeneratingOTP(false);
            handleApiExceptions("Something went wrong! Please try again.");
        });
    };

    const authenticate = () => {
        if (!authKey) {
            handleApiExceptions(`${payoutLink.bypass_authentication ? "OTP " : "Authentication Key"} is mandatory.`);
            return;
        }
        setIsAuthenticating(true);
        MerchantService.authenticate_payout_link({
            code: props.match.params.white_label_code,
            ulc: props.match.params.ulc,
            [payoutLink.bypass_authentication ? "otp" : "authentication_key"]: authKey,
            session_id: payoutLink.session_id
        }).then(response => {
            if (response.success) setActivePanel("1");
            else handleApiExceptions(response.message || "Something went wrong.");
            setIsAuthenticating(false);
        }).catch(error => {
            handleApiExceptions("Something went wrong! Please try again.");
            setIsAuthenticating(false);
        });
    };

    const initiate = (e) => {
        e.preventDefault();
        form.validateFields((err, values) => {
            if (err) return;
            if (values.ifsc) {
                values.ifsc = values.ifsc.toUpperCase();
            }
            setIsLoading(true);
            MerchantService.initiate_payout_link_attempt({
                code: props.match.params.white_label_code,
                ulc: props.match.params.ulc,
                session_id: payoutLink.session_id,
                ...values
            }).then(response => {
                if (!response.success) {
                    if (response.is_deactivated) setPayoutLink({...payoutLink, status: "deactivated"});
                    else handleApiExceptions("Could not complete transfer. Please try again.");
                    setIsLoading(false);
                    return;
                }
                setPayoutLink({...payoutLink, is_attempt_in_process: true});
            }).catch(error => {
                handleApiExceptions("Something went wrong! Please try again.");
                setIsLoading(false);
            });
        });
    };

    const renderBankAccountDetailsForm = () => {
        return (
            <>
                <Form.Item label="Account Number">
                    {getFieldDecorator("account_number", {
                        rules: validations.account_number
                    })(<Input autoComplete='off' prefix={<Icon type="bank" />} />)}
                </Form.Item>
                <Form.Item label="Account IFSC Code">
                    {getFieldDecorator("ifsc", {
                        rules: validations.ifsc
                    })(<Input autoComplete='off' prefix={<Icon type="bank" />} />)}
                </Form.Item>
            </>
        )
    };

    const renderUpiDetailsForm = () => {
        return (
            <Form.Item label="UPI ID">
                {getFieldDecorator("upi_handle", {
                    rules: validations.upi_handle
                })(<Input autoComplete='off' id="upi_handle" prefix={<Icon type="qrcode" />} />)}
            </Form.Item>
        )
    };

    const renderAuthentication = () => {
        return (
            <div>
                {
                    OTPSent ?
                        <>
                            <Text4 className="d-block mb-2">
                                {
                                    !payoutLink.bypass_authentication ?
                                        "Enter the Authentication Key provided to you."
                                        :
                                        `OTP has been sent to ${payoutLink.payee_phone ? payoutLink.payee_phone : payoutLink.payee_email}`
                                }
                            </Text4>
                            <Input
                                autoComplete='off'
                                ref={authInput}
                                prefix={<Icon type="key" />}
                                placeholder={`Enter ${payoutLink.bypass_authentication ? "OTP" : "Authentication Key"}`}
                                onChange={e => setAuthKey(e.target.value)}
                                onPressEnter={authenticate}
                                addonAfter={payoutLink.bypass_authentication ? renderCountdown(): null}
                            />
                            <Button className="mt-4" type="primary" block onClick={authenticate} loading={isAuthenticating}>Proceed</Button>
                        </>
                        :
                        <>
                            <Text4 className="d-block mb-2">
                                An OTP will be sent to {payoutLink.payee_phone ? payoutLink.payee_phone : payoutLink.payee_email}
                            </Text4>
                            <Button type="primary" block onClick={generateOTP} loading={isGeneratingOTP}>Send OTP</Button>
                        </>
                }
            </div>

        )
    };

    const renderForm = () => {
        return (
            <Form onSubmit={initiate}>
                <Form.Item>
                    {getFieldDecorator("beneficiary_type", {
                        rules: validations.beneficiary_type
                    })(
                        payoutLink.allowed_beneficiary_types.length > 1 ?
                            <Radio.Group>
                                {isBankTypeAllowed && <Radio.Button value="bank_account">Bank Account</Radio.Button>}

                                {isUpiTypeAllowed && (
                                    <Radio.Button value="upi">UPI</Radio.Button>
                                )}
                            </Radio.Group>
                            :
                            <Input autoComplete='off' type="hidden" value={payoutLink.allowed_beneficiary_types[0]} />
                    )}
                </Form.Item>

                <Form.Item label="Account Holder Name">
                    {getFieldDecorator("beneficiary_name", {
                        rules: [
                            { required: true, message: "Account Holder Name is required." },
                            { pattern: beneficiaryNameRegex(), message: "Please enter a valid name." },
                            { max: 50, message: "Can be of maximum 50 characters" }
                        ]
                    })(<Input autoComplete='off' id="account_holder_name" prefix={<Icon type="user" />} />)}
                </Form.Item>
                
                {form.getFieldValue("beneficiary_type") == "bank_account" ? renderBankAccountDetailsForm() : renderUpiDetailsForm()}

                <Button className="w-100" type="primary" htmlType="submit" loading={isLoading}>Submit</Button>
            </Form>
        )
    }

    const renderPage = () => {
        return (
            <Card className="payout-attempt-card-wrapper">
                <div className="payout-attempt-card d-flex justify-content-between">
                    <div className="details-wrapper" style={{ maxWidth: 500 }}>
                        <div className="mb-2" style={{ width: 150, height: "auto", overflow: "hidden", textAlign: "left", maxHeight: 65 }}>
                            <img style={{ margin: "auto", width: "auto", maxWidth: "100%", maxHeight: 65 }} alt="" src={merchant.logo_url || EASEBUZZ_PS_LOGO_COLOR} />
                        </div>
                        <div className="mb-1"><Text2>{merchant.name}</Text2><Text3> wants to send you</Text3></div>
                        <div className="mb-2"><Text1 style={{ color: "green" }}>{printRupee(payoutLink.amount)}</Text1></div>
                        {
                            isMobile ?
                                <Typography.Paragraph ellipsis={{ rows: 1, expandable: true }}>
                                    {payoutLink.description ? <>{payoutLink.description}<br /><br /></> : null}
                                    {isBankTypeAllowed && isUpiTypeAllowed ?
                                    "Enter your Account Number and IFSC or UPI handle to receive money directly to your bank account."
                                    :isBankTypeAllowed ? "Enter your Account Number and IFSC to receive money directly to your bank account." : "Enter your  UPI handle to receive money directly to your bank account."}
                                </Typography.Paragraph>
                                :
                                <Typography.Paragraph>
                                    {payoutLink.description ? <>{payoutLink.description}<br /><br /></> : null}
                                    {isBankTypeAllowed && isUpiTypeAllowed ?
                                    "Enter your Account Number and IFSC or UPI handle to receive money directly to your bank account."
                                    :isBankTypeAllowed ? "Enter your Account Number and IFSC to receive money directly to your bank account." : "Enter your  UPI handle to receive money directly to your bank account."}
                                </Typography.Paragraph>
                        }
                    </div>
                    <Card className="attempt-form">
                        <Collapse accordian={true} activeKey={activePanel} collapsible={false}>
                            <Collapse.Panel showArrow={false} key="0" header={<Text3>1. Authenticate {activePanel == "1" ? <Icon type="check-circle" theme="twoTone" twoToneColor="#52c41a" /> : null}</Text3>}>
                                {renderAuthentication()}
                            </Collapse.Panel>
                            <Collapse.Panel showArrow={false} key="1" header={<Text3>2. Provide Account Details</Text3>}>
                                {renderForm()}
                            </Collapse.Panel>
                        </Collapse>
                    </Card>
                </div>
            </Card>
        )
    };

    const renderLoader = () => {
        return (
            <div
                style={{
                    width: "100%",
                    height: "100vh",
                    display: "flex",
                    justifyContent: "center"
                }}
            >
                <EbPageLoader />
            </div>
        )
    };

    const renderInfo = () => {
        let infoStatus = "error";
        let infoTitle = "Oops!!!";
        let infoSubtitle = "Something went wrong.";

        if (payoutLink.status == "paid") {
            infoStatus = "success";
            infoTitle = "Transfer Processed";
            infoSubtitle = "Fund transfer request has been processed successfully and fund has been credited to your provided bank account.";
        }
        else if (payoutLink.status == "expired") {
            infoStatus = "error";
            infoTitle = "Link Expired";
            infoSubtitle = "The link has been expired and could not be used anymore.";
        }
        else if (payoutLink.status == "deactivated") {
            infoStatus = "error";
            infoTitle = "Link Deactivated";
            infoSubtitle = "The link has been deactivated and could not be used anymore.";
        }

        if (payoutLink.is_attempt_in_process) {
            infoStatus = "info";
            infoTitle = "Transfer Initiated";
            infoSubtitle = "Fund transfer has been initiated. You should receive fund in your bank account shortly.";
        }

        return (
            <div className="result-card-wrapper">
                <Card
                    className="result-card"
                    style={{background: payoutLink.status == "paid" ? "rgb(82, 196, 26, 0.2)" : payoutLink.is_attempt_in_process ? "rgb(24, 144, 255, 0.2)" : "rgb(245, 34, 45, 0.2)"}}
                >
                    <Result
                        style={{ color: "black" }}
                        status={infoStatus}
                        title={infoTitle}
                        subTitle={infoSubtitle}
                    />
                </Card>
            </div>
        )
    };

    const renderCountdown = () => showResetButton ?
        <Button className="m-0" type="link" size="small" onClick={handleResend}>Resend OTP</Button>
        :
        <Statistic.Countdown
            valueStyle={{ fontSize: 14, opacity: 0.8 }}
            value={countdown}
            format="mm:ss"
            onFinish={() => setShowResetButton(true)}
        />

    const renderLogo = () => {
        return (
            <div className="footer-logo-wrapper">
                <span className="mr-2" style={{ fontSize: 15 }}>Powered by:</span>
                <div style={{ width: 115, height: "auto", overflow: "hidden", textAlign: "center", maxHeight: 65 }}>
                    <img style={{ margin: "auto", width: "auto", maxWidth: "100%", maxHeight: 65 }} alt="" src={EASEBUZZ_PS_LOGO_COLOR} />
                </div>
            </div>
        )
    }

    useEffect(() => {
        getPayoutLinkInfo();
    }, []);

    return (
        <div className="payout-attempt-page-wrapper">
            { isMounted ? payoutLink.status == "active" && !payoutLink.is_attempt_in_process ? renderPage() : renderInfo() : renderLoader() }
            { renderLogo() }
        </div>
        
    )
};

const PayoutLinkPaymentAttempt = Form.create()(PayoutLinkPaymentAttemptForm);

export default PayoutLinkPaymentAttempt;